import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import ReasonDialog from "../../components/admin/ReasonDialog";
import InvoiceList from "../../components/billing/InvoiceList";
import { fmtDate } from "../../lib/labels";
import { INTERVAL, LIMIT, SUB_STATUS, Pill, limitText, money } from "../../lib/billingLabels";

const Current = ({ data, reload }) => {
  const s = data.subscription;
  const [cancel, setCancel] = useState(false);
  const call = async (url, body, msg) => {
    try { await api.post(url, body); toast.success(msg); setCancel(false); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const end = s.status === "trial" ? s.trial_ends_at : s.current_period_end;
  return (
    <div className="card-surface p-6" data-testid="billing-current">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-2xl font-bold">{s.plan_name}</p><Pill map={SUB_STATUS} value={s.status} testId="billing-status" />
        <span className="text-sm text-slate-500">{money(s.price, s.currency)} HT · {INTERVAL[s.interval]} · TVA {s.tax_rate} %</span>
      </div>
      <p className="mt-2 text-sm text-slate-600">{s.status === "trial" ? `Essai jusqu'au ${fmtDate(end, false)}` : end ? `Période en cours jusqu'au ${fmtDate(end, false)}` : ""}
        {s.discount && ` · ${s.discount.label} : ${s.discount.value}${s.discount.type === "percent" ? " %" : ` ${s.currency.toUpperCase()}`}`}
        {s.cancel_at_period_end && " · résiliation programmée à échéance"}{s.pending_plan_id && " · changement de formule programmé au prochain renouvellement"}</p>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="billing-usage">
        {["agencies", "members", "feedbacks_month"].map((k) => (
          <div key={k} className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">{LIMIT[k]}</p><p className="font-display text-lg font-bold">{data.usage[k]} / {limitText(s.limits[k])}</p></div>))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><Switch checked={s.auto_renew} onCheckedChange={(v) => call("/company/billing/auto-renew", { enabled: v }, "Préférence enregistrée")} data-testid="billing-auto-renew-switch" />Renouvellement automatique</label>
        {!["cancelled", "expired"].includes(s.status) && (s.cancel_at_period_end
          ? <Button size="sm" variant="outline" onClick={() => call("/company/billing/resume", {}, "Résiliation annulée")} data-testid="billing-resume-btn">Annuler la résiliation</Button>
          : <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => setCancel(true)} data-testid="billing-cancel-btn">Résilier à échéance</Button>)}
      </div>
      {cancel && <ReasonDialog testId="billing-cancel" title="Résilier à la fin de la période" label="Confirmer la résiliation" placeholder="Pourquoi souhaitez-vous résilier ?"
        onConfirm={(reason) => call("/company/billing/cancel", { reason }, "Résiliation programmée")} onClose={() => setCancel(false)} />}
    </div>
  );
};

const Plans = ({ data, reload }) => {
  const s = data.subscription;
  const choose = async (id) => {
    try { const { data: r } = await api.post("/company/billing/plan", { plan_id: id }); toast.success(r.detail); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="billing-plans">
      {data.plans.map((p) => (
        <div key={p.id} className={`card-surface p-5 ${s?.plan_id === p.id ? "ring-2 ring-blue-600" : ""}`} data-testid={`billing-plan-${p.code}`}>
          <p className="font-display text-lg font-semibold">{p.name}</p>
          <p className="mt-1 font-display text-2xl font-bold">{money(p.price, p.currency)}<span className="text-sm font-normal text-slate-500"> HT / {INTERVAL[p.interval].toLowerCase()}</span></p>
          {p.country_specific && <p className="text-xs text-indigo-700">Tarif pour votre pays</p>}
          <ul className="mt-3 space-y-1 text-sm">{p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-emerald-600" />{f}</li>)}</ul>
          <p className="mt-3 text-xs text-slate-500">{Object.entries(LIMIT).map(([k, l]) => `${l} : ${limitText(p.limits[k])}`).join(" · ")}</p>
          {s && s.plan_id !== p.id && s.pending_plan_id !== p.id && <Button size="sm" className="mt-4" onClick={() => choose(p.id)} data-testid={`billing-choose-${p.code}`}>{s.status === "trial" ? "Choisir" : "Passer à cette formule"}</Button>}
          {s?.pending_plan_id === p.id && <p className="mt-4 text-xs font-medium text-amber-700">Prévu au prochain renouvellement</p>}
          {s?.plan_id === p.id && <p className="mt-4 text-xs font-medium text-blue-700">Formule actuelle</p>}
        </div>))}
    </div>
  );
};

const PayDialog = ({ invoice, onClose }) => {
  const { data, loading, error } = useApi(`/company/billing/invoices/${invoice.id}/pay`);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent data-testid="billing-pay-dialog">
        <DialogHeader><DialogTitle>Régler la facture {invoice.number}</DialogTitle></DialogHeader>
        {loading && <Loader />}{error && <ErrorState message={error} />}
        {data && <div className="space-y-3 text-sm">
          <p>Solde à régler : <b>{money(data.balance, data.currency)}</b> · référence à indiquer : <b className="font-mono">{data.reference}</b></p>
          <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3" data-testid="billing-pay-instructions">{data.instructions}</p>
          <p className="text-xs text-slate-500">Le paiement en ligne n'est pas encore disponible. La facture passe en « Payée » uniquement après confirmation de la réception des fonds par notre équipe.</p>
        </div>}
      </DialogContent>
    </Dialog>
  );
};

export default function Billing() {
  const { data, loading, error, reload } = useApi("/company/billing");
  const [pay, setPay] = useState(null);
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="space-y-8">
      <PageHeader title="Abonnement & factures" description="Formule, consommation, factures et règlements de votre espace." />
      {data.subscription ? <Current data={data} reload={reload} /> : <p className="card-surface p-5 text-sm text-slate-500" data-testid="billing-no-sub">Votre abonnement démarrera à la validation de votre entreprise.</p>}
      <div><p className="mb-3 font-display text-lg font-semibold">Formules</p><Plans data={data} reload={reload} /></div>
      <div className="card-surface"><p className="p-4 font-display text-lg font-semibold">Factures</p>
        <InvoiceList items={data.invoices} pdfBase="/company/billing/invoices" testId="company-invoices"
          actions={(i) => !["paid", "void"].includes(i.status) && <Button size="sm" onClick={() => setPay(i)} data-testid={`invoice-pay-${i.number}`}>Payer</Button>} />
      </div>
      {data.history.length > 0 && <div className="card-surface p-5" data-testid="billing-history"><p className="font-display font-semibold">Historique</p>
        <ul className="mt-2 divide-y text-sm">{data.history.map((h, i) => <li key={i} className="py-2"><span className="text-slate-400">{fmtDate(h.at)}</span> · {h.detail} <span className="text-xs text-slate-400">({h.actor})</span></li>)}</ul></div>}
      {pay && <PayDialog invoice={pay} onClose={() => setPay(null)} />}
    </div>
  );
}
